import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import patientsContext from '../../store/patientsContext';
import AuthContext from '../../store/authContext';
import { MeetingDTO } from '../../../Models/MeetingModel';
import ApiClient from '../../Services/ApiClient';
import ConfirmModal from '../ui/confirmModal';
import { useAlert } from '../ui/alertProvider';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store';
import { fetchMeetingsByPatient, addMeeting, updateMeeting, removeMeeting, createZoomMeeting, sendWhatsAppForMeeting, sendWhatsAppForPayment } from '../../store/meetingsSlice';
import { selectMeetingsByPatient, selectCurrentPageForPatient } from '../../store/selectors';
import type { RootState } from '../../store';
import '../../style/meetings.css';
import '../../style/modal.css';
import '../../style/utils.css';
import { validateMeetingFields } from '../../Utils/validation';

const initialMeetingState = (patientId: number | null): MeetingDTO => ({
    id: 0,
    title: '',
    summary: '',
    meetingDate: '',
    isPaid: false,
    paymentId: 0,
    patientId: patientId ?? 0,
    description: ''
});

const Meetings: React.FC = () => {
    const navigate = useNavigate();
    const auth = React.useContext(AuthContext);
    const { isAdmin, isPatient } = auth as any;
    const [loading, error, patients] = React.useContext(patientsContext) as any;
    const { showAlert } = useAlert();

    const [patientId, setPatientId] = React.useState<number | null>(null);
    // meetings are now stored in Redux slice (per-patient)
    const dispatch = useDispatch<AppDispatch>();
    const meetingsState = useSelector(selectMeetingsByPatient(patientId));
    const meetings = meetingsState.items;
    const meetingsLoading = meetingsState.loading ?? false;
    const [whatsAppEnabled, setWhatsAppEnabled] = React.useState<boolean>(false);

    // pagination & other states
    const [page, setPage] = React.useState(1);
    const [pageSize] = React.useState(8);
    const totalCount = meetingsState.totalCount ?? 0;

    // edit / add state
    const [editingId, setEditingId] = React.useState<number | null>(null);
    const [editForm, setEditForm] = React.useState<MeetingDTO>(initialMeetingState(null));
    const [showAddModal, setShowAddModal] = React.useState(false);
    const [addForm, setAddForm] = React.useState<MeetingDTO>(initialMeetingState(null));

    // zoom modal state (kept if present)
    const [showZoomModal, setShowZoomModal] = React.useState(false);
    const [zoomMeetingId, setZoomMeetingId] = React.useState<number | null>(null);
    const [zoomDateLocal, setZoomDateLocal] = React.useState<string>('');
    const [zoomLoading, setZoomLoading] = React.useState(false);
    const [printHtml, setPrintHtml] = React.useState<string | null>(null);
    const [printTitle, setPrintTitle] = React.useState<string>('');
    const [selectedMeeting, setSelectedMeeting] = React.useState<any | null>(null);

    // Redirect to home if token missing/invalid
    React.useEffect(() => {
        const token = auth?.token ?? localStorage.getItem('token');
        try {
            const expired = auth && typeof auth.isTokenExpired === 'function'
                ? auth.isTokenExpired(token ?? '')
                : true;
            if (!token || expired) {
                navigate('/');
            }
        } catch {
            navigate('/');
        }
        // run only on mount / token change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.token]);

    const openPrintPreview = (html: string, title = '') => {
        setPrintHtml(html);
        setPrintTitle(title);
    };

    const printMeeting = (m: any) => {
        const html = `
            <html>
            <head>
              <meta charset="utf-8" />
              <title>Meeting ${m.id}</title>
              <style>
                body{font-family: Arial, sans-serif; direction: rtl; padding:20px}
                .card{max-width:800px;margin:0 auto;padding:20px;border:1px solid #ddd;border-radius:8px}
                h1{font-size:20px}
                table{width:100%;border-collapse:collapse}
                td{padding:8px;border-bottom:1px solid #f0f0f0}
              </style>
            </head>
            <body>
              <div class="card">
                <h1>פרטי פגישה - ${m.summary}</h1>
                <table>
                  <tr><td style="width:180px;font-weight:700">Id</td><td>${m.id}</td></tr>
                  <tr><td style="font-weight:700">תקציר</td><td>${m.summary}</td></tr>
                  <tr><td style="font-weight:700">תיאור</td><td>${m.description}</td></tr>
                  <tr><td style="font-weight:700">תאריך פגישה</td><td>${new Date(m.meetingDate).toLocaleString()}</td></tr>
                  <tr><td style="font-weight:700">שולם</td><td>${m.isPaid ? 'כן' : 'לא'}</td></tr>
                  <tr><td style="font-weight:700">קוד תשלום</td><td>${m.paymentId ?? ''}</td></tr>
                </table>
              </div>
            </body>
            </html>
        `;
        openPrintPreview(html, `פגישה ${m.id}`);
    };

    const printAllMeetings = async () => {
        if (!patientId) return;
        try {
            const payload: any = await dispatch(fetchMeetingsByPatient({ patientId, page: 1, pageSize: 1000 })).unwrap();
            const items = payload?.result?.items ?? [];
            const sb: string[] = [];
            sb.push('<html><head><meta charset="utf-8"/><style>body{font-family:Arial,Helvetica,sans-serif;direction:rtl} .card{border:1px solid #ddd;padding:12px;margin:12px;border-radius:6px}</style></head><body>');
            sb.push('<h1>כל הפגישות של המטופל</h1>');
            items.forEach((m: any) => {
                sb.push(`<div class="card"><h3>${m.summary}</h3><div>Id: ${m.id}</div><div>תאריך: ${new Date(m.meetingDate).toLocaleString()}</div><div>תיאור: ${m.description}</div></div>`);
            });
            sb.push('</body></html>');
            openPrintPreview(sb.join('\n'), 'כל הפגישות');
        } catch (err) {
            alert('שגיאה ביצירת הדפסה');
        }
    };

    React.useEffect(() => {
        // load whatsapp enabled flag
        (async () => {
            try {
                const res: any = await ApiClient.get('/whatsapp/enabled');
                setWhatsAppEnabled(!!res?.enabled);
            } catch { setWhatsAppEnabled(false); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // when patient selection changes, restore saved page from slice (if any)
    const storedPage = useSelector((state: RootState) => selectCurrentPageForPatient(patientId)(state));
    React.useEffect(() => {
        if (patientId !== null) {
            setPage(storedPage ?? 1);
        } else {
            setPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId, storedPage]);

    // fetch whenever patientId or page changes
    React.useEffect(() => {
        if (patientId !== null) {
            dispatch(fetchMeetingsByPatient({ patientId, page, pageSize }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId, page]);

    const handlePatientSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedId = event.target.value;
        const id = selectedId ? parseInt(selectedId) : null;
        setPatientId(id);
        setPage(1);
        setEditForm(initialMeetingState(id));
        setAddForm(initialMeetingState(id));
    };

    // fetching is handled by Redux thunk fetchMeetingsByPatient

    const onDelete = async (id: number) => {
        // show confirm modal instead of inline confirm
        setConfirmDeleteId(id);
    };

    const [confirmDeleteId, setConfirmDeleteId] = React.useState<number | null>(null);
    const [confirmDeleting, setConfirmDeleting] = React.useState(false);

    const performDeleteConfirmed = async (id: number | null) => {
        if (!id) return;
        setConfirmDeleting(true);
        try {
            await dispatch(removeMeeting(id)).unwrap();
            const newTotal = Math.max(0, (meetingsState.totalCount ?? 0) - 1);
            const lastPage = Math.max(1, Math.ceil(newTotal / pageSize));
            if (page > lastPage) setPage(lastPage);
            // refresh current page
            if (patientId) dispatch(fetchMeetingsByPatient({ patientId, page, pageSize }));
        } catch {
            showAlert('שגיאה במחיקת הפגישה', 'error');
        } finally {
            setConfirmDeleting(false);
            setConfirmDeleteId(null);
        }
    };

    const onEdit = (m: any) => {
        setEditingId(m.id);
        setEditForm({
            id: m.id,
            title: m.title ?? '',
            summary: m.summary ?? '',
            description: m.description ?? '',
            meetingDate: (m.meetingDate ?? '').slice(0, 16),
            isPaid: !!m.isPaid,
            paymentId: m.paymentId ?? 0,
            patientId: m.patientId ?? patientId ?? 0
        });
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type, checked } = e.target as any;
        setEditForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const saveEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingId) return;
        // client-side validation
        const errs = validateMeetingFields(editForm.title, editForm.summary, editForm.description);
        if (Object.keys(errs).length > 0) {
            showAlert(Object.values(errs).join('; '), 'error');
            return;
        }
        try {
            const payload: MeetingDTO = {
                id: editForm.id,
                title: editForm.title,
                summary: editForm.summary,
                description: editForm.description,
                meetingDate: editForm.meetingDate,
                isPaid: editForm.isPaid,
                paymentId: editForm.paymentId,
                patientId: editForm.patientId
            };
            await dispatch(updateMeeting(payload)).unwrap();
            setEditingId(null);
            if (patientId) dispatch(fetchMeetingsByPatient({ patientId, page, pageSize }));
        } catch {
            showAlert('שגיאה בעדכון הפגישה', 'error');
        }
    };

    const openAdd = () => {
        setShowAddModal(true);
        setAddForm(initialMeetingState(patientId));
    };

    const handleAddChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type, checked } = e.target as any;
        setAddForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const saveAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        // client-side validation
        const errs = validateMeetingFields(addForm.title, addForm.summary, addForm.description);
        if (Object.keys(errs).length > 0) {
            showAlert(Object.values(errs).join('; '), 'error');
            return;
        }
        try {
            const payload: MeetingDTO = {
                id: 0,
                title: addForm.title,
                summary: addForm.summary,
                description: addForm.description,
                meetingDate: addForm.meetingDate,
                isPaid: addForm.isPaid,
                paymentId: addForm.paymentId,
                patientId: patientId ?? 0
            };
            // optimistic add: generate a temporary negative id and pass it through meta
            const tempId = -Date.now();
            await dispatch(addMeeting({ meeting: payload, tempId })).unwrap();
            setShowAddModal(false);
            setPage(1);
            // no need to re-fetch: optimistic update inserted the item and fulfilled will replace it
            if (patientId) dispatch(fetchMeetingsByPatient({ patientId, page: 1, pageSize }));
        } catch {
            showAlert('שגיאה ביצירת פגישה', 'error');
        }
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const goToPage = (p: number) => {
        if (p < 1) p = 1;
        if (p > totalPages) p = totalPages;
        setPage(p);
    };

    if (loading) return <p>טוען...</p>;
    if (isPatient) {
        // Patient sees their own meetings via /meetings/me (handled in PatientDashboard or a similar view)
        return <p>אין הרשאה לגישה לממשק זה; עבור לתצוגת המטופל בדף המטופלים.</p>;
    }
    if (!isAdmin) return <p>אין הרשאה לצפות בדף זה</p>;
    if (error) {
        if (error.message === 'Not Found') {
            return <p>לא נמצאו פגישות</p>;
        }
        else {
            return <p>שגיאה: {error.message}</p>;
        }
    }

    return (
        <div className="rtl meetings-root">
            <h2>פגישות לפי מטופל</h2>

            <div className="controls-row">
                <select value={patientId ?? ''} onChange={handlePatientSelect}>
                    <option value="">בחר מטופל</option>
                    {patients && patients.map((patient: any) => (
                        <option key={patient.id} value={patient.id}>
                            {patient.firstName} {patient.lastName}
                        </option>
                    ))}
                </select>

                {patientId && <button className="btn ml-8" onClick={openAdd}>צור פגישה</button>}
                {patientId && <button className="btn ml-8" onClick={printAllMeetings}>הדפס את כל הפגישות</button>}
            </div>

            {meetingsLoading && <p>טוען פגישות...</p>}

            {!meetingsLoading && patientId && (
                <>
                    {meetings && meetings.length > 0 ? (
                        <>
                            <div className="meetings-grid">
                                {meetings.map((m: any) => (
                                    <div key={m.id} className="meeting-card">
                                        <h4 className="meeting-item-title">{m.summary}</h4>
                                        <p className="description">{m.description}</p>
                                        <p className="date">{new Date(m.meetingDate).toLocaleString()}</p>
                                        <div className="meeting-actions">
                                            <button onClick={() => onEdit(m)}>ערוך</button>
                                            <button onClick={() => onDelete(m.id)}>מחק</button>
                                            <button onClick={() => { setZoomMeetingId(m.id); setZoomDateLocal((new Date(m.meetingDate ?? Date.now())).toISOString().slice(0,16)); setShowZoomModal(true); }}>Create Zoom Meeting</button>
                                            <button onClick={() => printMeeting(m)}>הדפס</button>
                                            {whatsAppEnabled && (
                                                <button onClick={async () => {
                                                    try {
                                                        const custom = prompt('הקלד הודעה מותאמת אישית (השאר ריק להודעת ברירת המחדל)');
                                                        await dispatch(sendWhatsAppForMeeting({ meetingId: m.id, patientId: m.patientId, message: custom ?? undefined })).unwrap();
                                                        showAlert('WhatsApp נשלח בהצלחה', 'success');
                                                    } catch (err) {
                                                        showAlert('שליחת WhatsApp נכשלה', 'error');
                                                    }
                                                }}>שלח WhatsApp</button>
                                            )}
            <ConfirmModal
                open={confirmDeleteId != null}
                title={'Confirm delete'}
                message={<span>האם למחוק פגישה עם מזהה <strong>{confirmDeleteId}</strong>?</span>}
                onConfirm={() => performDeleteConfirmed(confirmDeleteId)}
                onCancel={() => setConfirmDeleteId(null)}
                loading={confirmDeleting}
            />
                                            {whatsAppEnabled && (
                                                <button onClick={async () => {
                                                    try {
                                                        await dispatch(sendWhatsAppForPayment({ meetingId: m.id, patientId: m.patientId })).unwrap();
                                                        showAlert('בקשת תשלום נשלחה ב-WhatsApp', 'success');
                                                    } catch (err) {
                                                        showAlert('שליחת בקשת תשלום ב-WhatsApp נכשלה', 'error');
                                                    }
                                                }}>שלח דרישת תשלום</button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination controls */}
                            <div className="pagination-controls">
                                <button onClick={() => goToPage(page - 1)} disabled={page <= 1}>Prev</button>
                                <span>עמוד {page} / {totalPages}</span>
                                <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>Next</button>
                            </div>
                        </>
                    ) : (
                        <p>לא נמצאו פגישות למטופל זה.</p>
                    )}
            {/* Details modal for meeting (mobile friendly) */}
            {selectedMeeting && (
                <div className="modal-overlay" onClick={() => setSelectedMeeting(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setSelectedMeeting(null)}>×</button>
                        <h3>{selectedMeeting.summary}</h3>
                        <div style={{ marginTop: 8 }}>
                            <div><strong>תיאור:</strong> {selectedMeeting.description}</div>
                            <div><strong>תאריך:</strong> {new Date(selectedMeeting.meetingDate).toLocaleString()}</div>
                            <div><strong>שולם:</strong> {selectedMeeting.isPaid ? 'כן' : 'לא'}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <button className="btn" onClick={() => { onEdit(selectedMeeting); setSelectedMeeting(null); }}>ערוך</button>
                            <button className="btn btn-danger" onClick={async () => { setConfirmDeleteId(selectedMeeting.id); }}>מחק</button>
                            <button className="btn" onClick={() => { setZoomMeetingId(selectedMeeting.id); setZoomDateLocal((new Date(selectedMeeting.meetingDate ?? Date.now())).toISOString().slice(0,16)); setShowZoomModal(true); }}>Create Zoom Meeting</button>
                            <button className="btn" onClick={() => printMeeting(selectedMeeting)}>הדפס</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Print preview modal */}
            {printHtml && (
                <div className="modal-overlay" onClick={() => { setPrintHtml(null); setPrintTitle(''); }}>
                    <div className="modal-content print-modal-content" onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => { setPrintHtml(null); setPrintTitle(''); }}>×</button>
                        <h3>{printTitle || 'הדפסה'}</h3>
                        <div className="print-preview-container">
                            <iframe title="print-preview" srcDoc={printHtml} className="print-iframe" />
                        </div>
                        <div className="mt-8" style={{ textAlign: 'right' }}>
                            <button onClick={() => {
                                const iframe = document.querySelector('iframe[title="print-preview"]') as HTMLIFrameElement | null;
                                try {
                                  iframe?.contentWindow?.focus();
                                  iframe?.contentWindow?.print();
                                } catch { window.print(); }
                            }}>הדפס</button>
                            <button className="ml-8" onClick={() => { setPrintHtml(null); setPrintTitle(''); }}>סגור</button>
                        </div>
                    </div>
                </div>
            )}
                </>
            )}

            {/* Edit modal */}
            {editingId !== null && (
                <div className="modal-overlay" onClick={() => setEditingId(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <button className="modal-close modal-close-button" onClick={() => setEditingId(null)}>×</button>
                        <h3>עריכת פגישה</h3>
                        <form onSubmit={saveEdit}>
                            <div>
                                <label>כותרת:</label>
                                <input name="title" value={editForm.title} onChange={handleEditChange} />
                            </div>
                            <div>
                                <label>תקציר:</label>
                                <textarea name="summary" value={editForm.summary} onChange={handleEditChange} required rows={5} />
                            </div>
                            <div>
                                <label>תיאור:</label>
                                <textarea name="description" value={editForm.description} onChange={handleEditChange} />
                            </div>
                            <div>
                                <label>תאריך פגישה:</label>
                                <input name="meetingDate" type="datetime-local"
                                    value={(editForm.meetingDate ?? '').slice(0, 16)}
                                    onChange={handleEditChange} required />
                            </div>
                            <div className="meeting-checkbox-row">
                                <label className="meeting-checkbox-label">שולם:</label>
                                <input className="meeting-checkbox-input" name="isPaid" type="checkbox" checked={editForm.isPaid} onChange={handleEditChange} />
                            </div>
                            <div>
                                <label>קוד תשלום:</label>
                                <input name="paymentId" type="number" value={editForm.paymentId ?? ''} onChange={handleEditChange} />
                            </div>
                            <div className="mt-8">
                                <button type="submit">שמור</button>
                                <button type="button" className="ml-8" onClick={() => setEditingId(null)}>ביטול</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <button className="modal-close modal-close-button" onClick={() => setShowAddModal(false)}>×</button>
                        <h3>יצירת פגישה</h3>
                        <form onSubmit={saveAdd}>
                            <div>
                                <label>כותרת:</label>
                                <input name="title" value={addForm.title} onChange={handleAddChange} />
                            </div>
                            <div>
                                <label>תקציר:</label>
                                <textarea name="summary" value={addForm.summary} onChange={handleAddChange} required rows={5} />
                            </div>
                            <div>
                                <label>תיאור:</label>
                                <textarea name="description" value={addForm.description} onChange={handleAddChange} />
                            </div>
                            <div>
                                <label>תאריך פגישה:</label>
                                <input name="meetingDate" type="datetime-local"
                                    value={(addForm.meetingDate ?? '').slice(0, 16)}
                                    onChange={handleAddChange} required />
                            </div>
                            <div className="meeting-checkbox-row">
                                <label className="meeting-checkbox-label">שולם:</label>
                                <input className="meeting-checkbox-input" name="isPaid" type="checkbox" checked={addForm.isPaid} onChange={handleAddChange} />
                            </div>
                            <div className="mt-8">
                                <button type="submit">צור</button>
                                <button type="button" className="ml-8" onClick={() => setShowAddModal(false)}>ביטול</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Zoom modal */}
            {showZoomModal && (
                <div className="modal-overlay" onClick={() => { setShowZoomModal(false); setZoomMeetingId(null); }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <button className="modal-close modal-close-button" onClick={() => { setShowZoomModal(false); setZoomMeetingId(null); }}>×</button>
                        <h3>Create Zoom Meeting</h3>
                        <div>
                            <label>Choose date & time:</label>
                            <input
                                type="datetime-local"
                                value={zoomDateLocal}
                                onChange={e => setZoomDateLocal(e.target.value)}
                            />
                        </div>
                        <div className="mt-12">
                            <button onClick={() => { setShowZoomModal(false); setZoomMeetingId(null); }}>Close</button>
                            <button className="ml-8" onClick={async () => {
                                if (!zoomMeetingId || !zoomDateLocal) { showAlert('בחר תאריך ושעה', 'error'); return; }
                                setZoomLoading(true);
                                try {
                                    const iso = new Date(zoomDateLocal).toISOString();
                                    await dispatch(createZoomMeeting({ meetingId: zoomMeetingId, meetingDate: iso })).unwrap();
                                    setShowZoomModal(false);
                                    setZoomMeetingId(null);
                                    setZoomDateLocal('');
                                    showAlert('פגישה נוצרה ונשלחה ל-Zoom', 'success');
                                } catch (err: any) {
                                    showAlert(err?.data?.message || err?.message || 'שגיאה ביצירת פגישת זום', 'error');
                                } finally { setZoomLoading(false); }
                            }}>{zoomLoading ? 'Creating...' : 'Create and Send'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Meetings;